import React from "react";

import {
  CloudUploadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { Typography, Tag } from "antd";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

const { Text } = Typography;

const Container = styled.div`
  width: 100%;
  padding: 12px 16px;
  background: #f5f5f5;
  border-radius: 8px;
  margin-top: 12px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Title = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #262626;
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
`;

const Stats = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px 8px;
`;

const StatItem = styled.div`
  font-size: 12px;
  color: #8c8c8c;

  .ant-tag {
    margin-inline-end: 0;
  }
`;

interface UploadQueueStats {
  pending: number;
  uploading: number;
  completed: number;
  failed: number;
}

interface UploadQueueStatusValue {
  stats: UploadQueueStats;
}

interface VideoUploadQueueStatusProps {
  uploadQueue: UploadQueueStatusValue;
}

/**
 * 影片片段（chunk）上傳 Queue 狀態顯示
 *
 * 顯示條件：有任何 pending / uploading / failed 任務時顯示。
 * Completed 自身不觸發顯示，但與其他狀態並存時跟著顯示計數。
 */
const VideoUploadQueueStatus: React.FC<VideoUploadQueueStatusProps> = ({
  uploadQueue,
}) => {
  const { t } = useTranslation();

  if (!uploadQueue) return null;

  const { stats } = uploadQueue;
  const total =
    stats.pending + stats.uploading + stats.completed + stats.failed;
  if (total === 0) return null;

  const hasActiveTasks =
    stats.pending > 0 || stats.uploading > 0 || stats.failed > 0;
  if (!hasActiveTasks) return null;

  return (
    <Container>
      <Header>
        <Title>
          <CloudUploadOutlined />
          {t("operator.qc.video_upload_queue.title", {
            defaultValue: "影片片段上傳",
          })}
        </Title>
        <Stats>
          <StatItem>
            <Tag color="processing" icon={<LoadingOutlined />}>
              {stats.uploading}{" "}
              {t("operator.qc.video_upload_queue.uploading", {
                defaultValue: "上傳中",
              })}
            </Tag>
          </StatItem>
          <StatItem>
            <Tag color="default">
              {stats.pending}{" "}
              {t("operator.qc.video_upload_queue.pending", {
                defaultValue: "待上傳",
              })}
            </Tag>
          </StatItem>
          <StatItem>
            <Tag color="success" icon={<CheckCircleOutlined />}>
              {stats.completed}{" "}
              {t("operator.qc.video_upload_queue.completed", {
                defaultValue: "已完成",
              })}
            </Tag>
          </StatItem>
          {stats.failed > 0 && (
            <StatItem>
              <Tag color="error" icon={<CloseCircleOutlined />}>
                {stats.failed}{" "}
                {t("operator.qc.video_upload_queue.failed", {
                  defaultValue: "失敗",
                })}
              </Tag>
            </StatItem>
          )}
        </Stats>
      </Header>
    </Container>
  );
};

export default VideoUploadQueueStatus;
